/**
 * ExperienceTimeline — single styling source of truth.
 *
 * The new (new-homepage path) timeline's own chrome colors and font weights,
 * consolidated so each value lives in exactly one place instead of being
 * copy-pasted as a literal across the render files.
 *
 * Intentionally self-contained: this is the new timeline's own visual system
 * and is kept INDEPENDENT of both the classic `components/Timeline` engine and
 * the app's MUI/emotion theme. The two timelines are styled differently on
 * purpose — do not wire this to `theme.custom.*`.
 *
 * Note: item / lane / actor colors are DATA-DRIVEN (passed in as props). Only
 * the timeline's own chrome lives here.
 */
export const timelineColors = {
  /** Dark chrome: tooltip/badge/pill surfaces, primary text & lines, selection ring. */
  ink: '#272C30',
  /** White: glyph fill, chip/pill text, lane-label background, control background. */
  surface: '#FFFFFF',
  /** Muted text: legend, plain ruler pill. */
  textMuted: '#5B6470',
  /** Subtle text: week header, lane collapse chevron. */
  textSubtle: '#778088',
  /** Subtle line: day gridline, control border. */
  lineSubtle: '#DDE0E3',
  /** Stronger line: week gridline, plain ruler tick. */
  lineStrong: '#B8BEC5',
  /** Anchor band column fill. */
  bandFill: '#DDE1E4',
  /** Hairline borders: annotation lane, week-header underline, plain ruler pill background. */
  border: '#EBEDEF',
  /** Frosted item-label chip. */
  labelChipBg: 'rgba(245,246,247,0.7)',
  labelChipBorder: 'rgba(227,229,232,0.7)',
  /** Tooltip drop shadow. */
  tooltipShadow: 'rgba(8,18,26,0.25)'
} as const;

/** Weight system for the timeline chrome (denser/lighter than the app default). */
export const timelineFontWeight = {
  body: 300,
  regular: 400,
  medium: 500,
  label: 600,
  title: 700
} as const;
